import { useMemo } from "react";
import type { MonthSpan } from "../types";

/**
 * Computes header spans and today-index from a workdays array.
 */
export function useWorkdayGrid(workdays: string[]) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const monthSpans: MonthSpan[] = useMemo(() => {
    if (workdays.length === 0) return [];
    const spans: MonthSpan[] = [];
    let currentKey = "";
    let startIdx = 0;

    for (let i = 0; i < workdays.length; i++) {
      const d = new Date(workdays[i] + "T00:00:00");
      const key = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      if (key !== currentKey) {
        if (currentKey) {
          spans.push({ label: currentKey, startIdx, colSpan: i - startIdx });
        }
        currentKey = key;
        startIdx = i;
      }
    }
    if (currentKey) {
      spans.push({ label: currentKey, startIdx, colSpan: workdays.length - startIdx });
    }
    return spans;
  }, [workdays]);

  const todayIdx: number | null = useMemo(() => {
    const idx = workdays.indexOf(today);
    return idx >= 0 ? idx : null;
  }, [workdays, today]);

  // Index of Monday of the current week (first workday of the week)
  const weekStartIdx: number | null = useMemo(() => {
    const d = new Date(today + "T00:00:00");
    const day = d.getDay(); // 0=Sun, 1=Mon, ...
    const diff = day === 0 ? 6 : day - 1; // days since Monday
    d.setDate(d.getDate() - diff);
    const monday = d.toISOString().slice(0, 10);
    const idx = workdays.indexOf(monday);
    // If Monday is a holiday, find the nearest workday in this week
    if (idx >= 0) return idx;
    for (let i = 0; i < workdays.length; i++) {
      if (workdays[i] >= monday) return i;
    }
    return null;
  }, [workdays, today]);

  return { monthSpans, todayIdx, weekStartIdx };
}
